import { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateDog } from '../../redux/slices/Dogs/dogsSlice';
import VaccinationManager from '../Dogs/VaccinationManager';

const DogDetailsModal = ({ isOpen, onClose, dog, onDogUpdate }) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const [updating, setUpdating] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [localDog, setLocalDog] = useState(dog);

  // Update localDog when dog prop changes (e.g., after refresh)
  useEffect(() => {
    if (dog) {
      setLocalDog(dog);
      setSaveSuccess(false);
    }
  }, [dog]); // only runs when dog reference changes

  const vaccines = useMemo(() => [
    { key: 'lastRabiesDate', label: t('dogDetailsModal.rabies'), recommendedIntervalDays: 365 },
    { key: 'lastMultipleDate', label: t('dogDetailsModal.multiple'), recommendedIntervalDays: 365 },
    { key: 'lastBordetellaDate', label: t('dogDetailsModal.bordetella'), recommendedIntervalDays: 180 },
    { key: 'lastGiardiaDate', label: t('dogDetailsModal.giardia'), recommendedIntervalDays: 180 },
    { key: 'lastDewormingDate', label: t('dogDetailsModal.deworming'), recommendedIntervalDays: 90 },
  ], [t]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return t('dogDetailsModal.notSet');
    return new Date(dateString).toLocaleDateString();
  }, [t]);

  const calculateDaysSince = useCallback((dateString) => {
    if (!dateString) return null;
    const given = new Date(dateString);
    const today = new Date();
    const diffDays = Math.floor((today - given) / (1000 * 60 * 60 * 24));
    return diffDays;
  }, []);

  const handleDateChange = (key, value) => {
    if (!localDog) return;
    setLocalDog({ ...localDog, [key]: value });
  };

  const handleSave = async () => {
    if (!localDog) return;
    setUpdating(true);
    setSaveSuccess(false);
    try {
      const dogData = {};
      vaccines.forEach(v => { dogData[v.key] = localDog[v.key] || ''; });
      await dispatch(updateDog({ id: localDog._id, dogData })).unwrap();
      setSaveSuccess(true);
      onDogUpdate(); // refresh profile in background
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Update failed', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleVaccinationUpdate = useCallback((updatedDog) => {
    setLocalDog(updatedDog);
    // Optionally call parent update to refresh profile
    onDogUpdate();
  }, [onDogUpdate]);

  const getStatus = (daysSince, intervalDays) => {
    if (daysSince === null) return 'notSet';
    if (daysSince > intervalDays * 1.1) return 'overdue';
    if (daysSince > intervalDays) return 'dueSoon';
    return 'upToDate';
  };

  const getStatusColor = (statusKey) => {
    switch (statusKey) {
      case 'overdue': return 'text-[#9A0002]';
      case 'dueSoon': return 'text-[#B08D57]';
      case 'upToDate': return 'text-[#3A6B4C]';
      default: return 'text-[#3A2A20]/40';
    }
  };

  if (!localDog) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                <div className="bg-[#9A0002] px-6 py-5 flex justify-between items-center">
                  <Dialog.Title className="font-['Fraunces'] text-xl font-semibold text-[#EFE6DD]">
                    {localDog.name} — {t('dogDetailsModal.medicalRecord')}
                  </Dialog.Title>
                  <button onClick={onClose} className="text-[#EFE6DD]/80 hover:text-[#EFE6DD] transition">
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="p-6">
                  {/* Basic info */}
                  <div className="flex flex-col md:flex-row gap-6 mb-8 items-start">
                    {localDog.image && (
                      <img
                        src={localDog.image}
                        alt={localDog.name}
                        className="w-24 h-24 object-cover rounded-full border-2 border-[#EFE6DD] shadow-sm"
                      />
                    )}
                    <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
                      <p className="text-[#3A2A20]">
                        <span className="text-[#3A2A20]/50">{t('profile.breed')}: </span>
                        {localDog.breed}
                      </p>
                      <p className="text-[#3A2A20]">
                        <span className="text-[#3A2A20]/50">{t('profile.weight')}: </span>
                        {localDog.weight} kg
                      </p>
                      <p className="text-[#3A2A20]">
                        <span className="text-[#3A2A20]/50">{t('profile.age')}: </span>
                        {localDog.age} {t('profile.years')}
                      </p>
                      <p className="text-[#3A2A20]">
                        <span className="text-[#3A2A20]/50">{t('profile.gender')}: </span>
                        {localDog.gender === 'M' ? t('profile.male') : t('profile.female')}
                      </p>
                    </div>
                  </div>

                  {/* Vaccination Table */}
                  <div className="overflow-x-auto rounded-xl border border-[#3A2A20]/10">
                    <table className="min-w-full divide-y divide-[#3A2A20]/10">
                      <thead className="bg-[#EFE6DD]/60">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60">
                            {t('dogDetailsModal.vaccine')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60">
                            {t('dogDetailsModal.lastGivenDate')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60">
                            {t('dogDetailsModal.daysSince')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60">
                            {t('dogDetailsModal.status')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/60">
                            {t('dogDetailsModal.updateDate')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#3A2A20]/10 bg-white">
                        {vaccines.map((vaccine) => {
                          const currentDate = localDog[vaccine.key];
                          const daysSince = currentDate ? calculateDaysSince(currentDate) : null;
                          const statusKey = getStatus(daysSince, vaccine.recommendedIntervalDays);
                          return (
                            <tr key={vaccine.key}>
                              <td className="px-4 py-3 font-medium text-[#3A2A20] text-sm">{vaccine.label}</td>
                              <td className="px-4 py-3 text-sm text-[#3A2A20]/70">{formatDate(currentDate)}</td>
                              <td className="px-4 py-3 text-sm text-[#3A2A20]/70">
                                {daysSince !== null ? `${daysSince} ${t('dogDetailsModal.days')}` : '—'}
                              </td>
                              <td className={`px-4 py-3 text-sm font-semibold ${getStatusColor(statusKey)}`}>
                                {t(`dogDetailsModal.${statusKey}`)}
                              </td>
                              <td className="px-4 py-3">
                                <input
                                  type="date"
                                  value={currentDate ? currentDate.split('T')[0] : ''}
                                  onChange={(e) => handleDateChange(vaccine.key, e.target.value)}
                                  className="border border-[#3A2A20]/15 rounded-lg p-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#9A0002]/40 focus:border-[#9A0002] transition"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Vaccination Manager (separate component) */}
                  <VaccinationManager dog={localDog} onUpdate={handleVaccinationUpdate} />

                  {saveSuccess && (
                    <div className="mt-4 px-4 py-2.5 bg-[#3A6B4C]/10 text-[#3A6B4C] rounded-xl text-center text-sm">
                      {t('dogDetailsModal.saveSuccess')}
                    </div>
                  )}

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      onClick={onClose}
                      className="px-5 py-2.5 text-sm font-medium text-[#3A2A20] border border-[#3A2A20]/20 rounded-xl hover:bg-[#3A2A20]/5 transition"
                    >
                      {t('common.close')}
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={updating}
                      className="px-5 py-2.5 text-sm font-medium bg-[#9A0002] hover:bg-[#7A0001] text-[#EFE6DD] rounded-xl transition disabled:opacity-50"
                    >
                      {updating ? t('dogDetailsModal.saving') : t('dogDetailsModal.saveChanges')}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DogDetailsModal;